import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, ShieldCheck } from "lucide-react";
import { platformListAuditEvents } from "../../lib/api";

/**
 * Sprint 1 — Platform Audit Log panel (admin-only).
 *
 * Reuses existing Editorial Residence tokens. No new public-facing UI.
 * Table columns: timestamp · actor · action · target · outcome · correlation ID.
 * Filters: action (freeform, safe), outcome (dropdown), date range (day-level).
 * Never exposes raw metadata by default.
 */
const PAGE_SIZE = 25;

const OUTCOMES = ["", "ok", "denied", "error"];

export default function AuditLogPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [outcomeFilter, setOutcomeFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async (nextOffset = 0) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (actionFilter.trim()) params.action = actionFilter.trim();
      if (outcomeFilter) params.outcome = outcomeFilter;
      if (fromDate) params.from = new Date(fromDate).toISOString();
      if (toDate) {
        // Extend end-of-day → treat `to` as exclusive upper bound at end of day + 1
        const d = new Date(toDate);
        d.setDate(d.getDate() + 1);
        params.to = d.toISOString();
      }
      const d = await platformListAuditEvents(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
      setOffset(nextOffset);
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 403) {
        setError({ kind: "denied", message: "Administrator privileges required." });
      } else if (body?.code) {
        setError({ kind: "api", message: `${body.code}: ${body.message}` });
      } else {
        setError({ kind: "network", message: "Could not reach the Platform API." });
      }
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); /* initial load */ }, []);

  const applyFilters = (e) => { e?.preventDefault?.(); load(0); };

  const canPrev = offset > 0;
  const canNext = offset + PAGE_SIZE < total;

  const startIdx = total === 0 ? 0 : offset + 1;
  const endIdx = Math.min(offset + PAGE_SIZE, total);

  return (
    <div data-testid="admin-audit-log">
      <header className="mb-7">
        <p className="ed-overline mb-2 flex items-center gap-2">
          <ShieldCheck size={12} strokeWidth={1.8} className="text-[var(--ed-emerald)]" />
          System
        </p>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--ed-ink)]">Audit Log</h1>
        <p className="text-[var(--ed-ink-muted)] text-sm mt-1.5 max-w-2xl">
          Append-only record of every administrator action and sensitive read on the Platform API.
          Rows are tenant-scoped and cannot be edited or deleted.
        </p>
      </header>

      {/* Filters */}
      <form
        onSubmit={applyFilters}
        className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-5 items-end"
      >
        <div>
          <label className="ed-overline text-[0.55rem] mb-1.5 block">Action</label>
          <input
            data-testid="audit-filter-action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder="e.g. feature_flag.update"
            className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          />
        </div>
        <div>
          <label className="ed-overline text-[0.55rem] mb-1.5 block">Outcome</label>
          <select
            data-testid="audit-filter-outcome"
            value={outcomeFilter}
            onChange={(e) => setOutcomeFilter(e.target.value)}
            className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          >
            {OUTCOMES.map((o) => (
              <option key={o || "any"} value={o}>{o || "Any"}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="ed-overline text-[0.55rem] mb-1.5 block">From</label>
          <input
            data-testid="audit-filter-from"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          />
        </div>
        <div>
          <label className="ed-overline text-[0.55rem] mb-1.5 block">To</label>
          <input
            data-testid="audit-filter-to"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="w-full text-sm px-3 py-2 border border-[var(--ed-rule)] bg-white focus:outline-none focus:border-[var(--ed-ink)] rounded-sm"
          />
        </div>
        <button
          data-testid="audit-apply-filters"
          type="submit"
          className="text-[0.7rem] uppercase tracking-[0.22em] py-2.5 px-4 bg-[var(--ed-ink)] text-white hover:bg-[var(--ed-emerald)] transition-colors rounded-sm flex items-center justify-center gap-2"
        >
          <RefreshCw size={12} />
          Apply
        </button>
      </form>

      {/* Table / states */}
      <div className="border border-[var(--ed-rule)] rounded-sm bg-white">
        <div className="px-4 py-3 border-b border-[var(--ed-rule)] flex items-center justify-between">
          <p className="text-xs text-[var(--ed-ink-muted)]" data-testid="audit-range">
            {loading ? "Loading…" : `Showing ${startIdx}–${endIdx} of ${total.toLocaleString()}`}
          </p>
          <div className="flex gap-2">
            <button
              data-testid="audit-page-prev"
              onClick={() => canPrev && load(Math.max(0, offset - PAGE_SIZE))}
              disabled={!canPrev || loading}
              className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm"
              aria-label="Previous page"
            >
              Prev
            </button>
            <button
              data-testid="audit-page-next"
              onClick={() => canNext && load(offset + PAGE_SIZE)}
              disabled={!canNext || loading}
              className="text-xs uppercase tracking-[0.2em] px-3 py-1.5 border border-[var(--ed-rule)] disabled:opacity-40 hover:border-[var(--ed-ink)] rounded-sm"
              aria-label="Next page"
            >
              Next
            </button>
          </div>
        </div>

        {error && (
          <div
            data-testid="audit-error"
            role="alert"
            className="p-6 flex items-start gap-3 text-sm text-[var(--ed-ink)]"
          >
            <AlertTriangle size={16} className="text-red-600 mt-0.5" />
            <div>
              <p className="font-medium mb-1">
                {error.kind === "denied" ? "Access denied" : "Could not load audit log"}
              </p>
              <p className="text-[var(--ed-ink-muted)]">{error.message}</p>
            </div>
          </div>
        )}

        {!error && !loading && items.length === 0 && (
          <p data-testid="audit-empty" className="p-8 text-center text-sm text-[var(--ed-ink-muted)]">
            No audit events yet for this tenant.
          </p>
        )}

        {!error && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[var(--ed-paper)]">
                <tr className="text-left text-[var(--ed-ink-muted)]">
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Time (UTC)</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Actor</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Action</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Target</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Outcome</th>
                  <th className="px-4 py-2.5 ed-overline text-[0.55rem]">Correlation</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-[var(--ed-rule)] hover:bg-[var(--ed-paper)]/50 transition-colors">
                    <td className="px-4 py-2.5 whitespace-nowrap font-mono text-[0.75rem] text-[var(--ed-ink-muted)]">
                      {it.occurred_at.replace("T", " ").slice(0, 19)}
                    </td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink)]">
                      <span className="text-[0.6rem] uppercase tracking-[0.2em] text-[var(--ed-ink-muted)] mr-1.5">
                        {it.actor_type}
                      </span>
                      <span className="font-mono text-[0.75rem]">
                        {it.actor_id ? it.actor_id.slice(0, 16) + "…" : "—"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[0.78rem]">{it.action}</td>
                    <td className="px-4 py-2.5 text-[var(--ed-ink-muted)]">
                      {it.target_id ? `${it.target_type}/${it.target_id}` : it.target_type}
                    </td>
                    <td className="px-4 py-2.5">
                      <OutcomePill outcome={it.outcome} />
                    </td>
                    <td className="px-4 py-2.5 font-mono text-[0.7rem] text-[var(--ed-ink-muted)]">
                      {it.correlation_id}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function OutcomePill({ outcome }) {
  const map = {
    ok:     "bg-[var(--ed-emerald-tint)] text-[var(--ed-emerald)] border-[var(--ed-emerald)]",
    denied: "bg-red-50 text-red-700 border-red-300",
    error:  "bg-amber-50 text-amber-800 border-amber-300",
  };
  const cls = map[outcome] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span
      className={`inline-block text-[0.6rem] uppercase tracking-[0.2em] px-2 py-0.5 border rounded-full ${cls}`}
      data-testid={`audit-outcome-${outcome}`}
    >
      {outcome}
    </span>
  );
}
